// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    Icon,
    Translate,
    Tooltip,
} from '@plesk/ui-library';
import { Loader } from 'common/components';
import {
    computeResources,
    IComputeResourceResponse,
    VirtualizationType,
} from 'common/api/resources/ComputeResource';
import { useLoadMore } from 'common/hooks/useLoadMore';
import {
    convertToDataUnit,
    DataUnit,
} from 'common/helpers/units';
import {
    ContentBody,
    SectionHeader,
    TableRow,
    TableCell,
    CellSubtext,
    LoadMoreLink,
    WarningBanner,
} from 'admin/failoverDomain/containers/FailoverDomainCreateDialog/Styles';
import {
    FAILOVER_DOMAIN,
    FAILOVER_DOMAIN_ROW,
} from 'admin/failoverDomain/constants/tests';
import { dataCySelector } from 'common/tests/selectors';
import { ICONS } from 'common/constants';

export type FailoverDomainCrIneligibilityReason = 'no_storage' | 'assigned_to_other_domain' | null;

/**
 * Derive why a Compute Resource cannot join the Failover Domain, or null if eligible.
 *
 * The server is the gate, not this predicate — it only drives advisory UX (disabled rows +
 * tooltips). Storage compatibility is enforced server-side by
 * FailoverDomainService::validateCrsCompatibility, and "already assigned to another domain"
 * by AvailableComputeResourcesRule on create/update. Location is enforced by pre-filtering the
 * CR query by location, so every CR reaching here is already in the domain's location.
 */
export const failoverDomainCrIneligibilityReason = (
    cr: IComputeResourceResponse,
    storageId: number,
    editingDomainId?: number
): FailoverDomainCrIneligibilityReason => {
    if (!cr.storages.some(storage => storage.id === storageId)) {
        return 'no_storage';
    }
    if (cr.failover_domain && cr.failover_domain.id !== editingDomainId) {
        return 'assigned_to_other_domain';
    }
    return null;
};

export interface IComputeResourceStepProps {
    storageId: number;
    locationId: number;
    selectedIds: number[];
    editingDomainId?: number;
    searchQuery: string;
    onSelectionChange: (ids: number[]) => void;
}

export const ComputeResourceStep: React.FC<IComputeResourceStepProps> = ({
    storageId,
    locationId,
    selectedIds,
    editingDomainId,
    searchQuery,
    onSelectionChange,
}) => {
    const {
        items: crs,
        isLoading,
        hasMore,
        loadMore,
    } = useLoadMore<IComputeResourceResponse>({
        fetchPage: (pageNum) => {
            const filters: Record<string, string | number> = {
                location: locationId,
                virtualization_type: VirtualizationType.KVM,
            };
            if (searchQuery) {
                filters.search = searchQuery;
            }

            return computeResources.list({ filters, page: pageNum });
        },
        enabled: !!storageId && !!locationId,
    }, [storageId, locationId, searchQuery]);

    const handleAdd = (cr: IComputeResourceResponse) => {
        if (failoverDomainCrIneligibilityReason(cr, storageId, editingDomainId) !== null) {
            return;
        }
        if (!selectedIds.includes(cr.id)) {
            onSelectionChange([...selectedIds, cr.id]);
        }
    };

    const handleRemove = (cr: IComputeResourceResponse) => {
        onSelectionChange(selectedIds.filter(id => id !== cr.id));
    };

    const selectedCrs = crs.filter(cr => selectedIds.includes(cr.id));
    const notSelectedCrs = crs.filter(cr => !selectedIds.includes(cr.id));

    const renderCrRow = (cr: IComputeResourceResponse, isSelected: boolean) => {
        const reason = failoverDomainCrIneligibilityReason(cr, storageId, editingDomainId);
        const disabled = reason !== null;

        let tooltipTitle: React.ReactNode = null;
        if (reason === 'no_storage') {
            tooltipTitle = <Translate content="failoverDomain.create.cr.noStorage" />;
        } else if (reason === 'assigned_to_other_domain') {
            tooltipTitle = <Translate content="failoverDomain.create.cr.alreadyInDomain" params={{ name: cr.failover_domain?.name }} />;
        }

        const actionButton = isSelected ? (
            <Icon
                name={ICONS.MINUS}
                onClick={() => handleRemove(cr)}
                style={{ cursor: 'pointer' }}
                data-cy={dataCySelector(cr.id, FAILOVER_DOMAIN_ROW.CR_REMOVE)}
            />
        ) : (
            disabled ? null : (
                <Icon
                    name={ICONS.PLUS}
                    onClick={() => handleAdd(cr)}
                    style={{ cursor: 'pointer' }}
                    data-cy={dataCySelector(cr.id, FAILOVER_DOMAIN_ROW.CR_ADD)}
                />
            )
        );

        const row = (
            <TableRow
                key={cr.id}
                isDisabled={disabled}
                data-cy={dataCySelector(cr.id, FAILOVER_DOMAIN_ROW.CR_ROW)}
            >
                <TableCell width="40px">
                    {actionButton}
                </TableCell>
                <TableCell width="60px">{cr.id}</TableCell>
                <TableCell flex={2}>
                    <div>{cr.name}</div>
                    <CellSubtext>{cr.host}</CellSubtext>
                </TableCell>
                <TableCell flex={1}>
                    <div>{cr.vms_count}</div>
                    <CellSubtext>
                        {cr.settings.virtualization_types.join(', ')}
                    </CellSubtext>
                </TableCell>
                <TableCell width="100px">
                    {`${convertToDataUnit(cr.metrics.memory.total, DataUnit.GiB)} ${DataUnit.GiB}`}
                </TableCell>
            </TableRow>
        );

        if (tooltipTitle) {
            return (
                <Tooltip key={cr.id} title={tooltipTitle}>
                    <div>{row}</div>
                </Tooltip>
            );
        }

        return row;
    };

    return (
        <ContentBody data-cy={FAILOVER_DOMAIN.CR_STEP}>
            <WarningBanner data-cy={FAILOVER_DOMAIN.CR_PROMOTION_NOTICE}>
                <Icon name={ICONS.INFO_CIRCLE} size={16} />
                <Translate content="failoverDomain.create.cr.promotionNotice" />
            </WarningBanner>
            <Loader isLoading={isLoading && crs.length === 0}>
                {selectedCrs.length > 0 && (
                    <>
                        <SectionHeader>
                            <Translate content="failoverDomain.create.cr.selected" />
                            {` (${selectedCrs.length})`}
                        </SectionHeader>
                        {selectedCrs.map(cr => renderCrRow(cr, true))}
                    </>
                )}
                {notSelectedCrs.length > 0 && (
                    <>
                        <SectionHeader>
                            <Translate content="failoverDomain.create.cr.notSelected" />
                            {` (${notSelectedCrs.length})`}
                        </SectionHeader>
                        {notSelectedCrs.map(cr => renderCrRow(cr, false))}
                    </>
                )}
                {hasMore && (
                    <TableRow>
                        <TableCell flex={1}>
                            <LoadMoreLink
                                onClick={loadMore}
                                isDisabled={isLoading}
                                data-cy={FAILOVER_DOMAIN.CR_LOAD_MORE}
                            >
                                {isLoading ? (
                                    <Icon name={ICONS.REFRESH} />
                                ) : (
                                    <Translate content="failoverDomain.create.cr.loadMore" />
                                )}
                            </LoadMoreLink>
                        </TableCell>
                    </TableRow>
                )}
                {!isLoading && crs.length === 0 && (
                    <TableRow>
                        <TableCell flex={1}>
                            <Translate content="failoverDomain.create.cr.empty" />
                        </TableCell>
                    </TableRow>
                )}
            </Loader>
        </ContentBody>
    );
};

export default ComputeResourceStep;
